import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X as XIcon } from 'lucide-react';
function PolicyModal({
  title,
  content,
  onClose
}: {
  title: string;
  content: React.ReactNode;
  onClose: () => void;
}) {
  return <AnimatePresence data-id="element-45">
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} data-id="element-46">
        <motion.div className="bg-dark-panel border border-dark-lighter w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-sm" initial={{
        scale: 0.95,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.95,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} data-id="element-47">
          <div className="sticky top-0 bg-dark-panel border-b border-dark-lighter flex items-center justify-between px-6 py-4" data-id="element-48">
            <h3 className="font-display text-2xl uppercase text-white" data-id="element-49">
              {title}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" data-id="element-50">
              <XIcon className="w-6 h-6" data-id="element-51" />
            </button>
          </div>
          <div className="px-6 py-6 text-gray-400 text-sm leading-relaxed space-y-4" data-id="element-52">
            {content}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
const privacyContent = <>
    <p className="text-white font-semibold text-base" data-id="element-53">Privacy Policy</p>
    <p data-id="element-54">
      Last updated:{' '}
      {new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })}
    </p>
    <p data-id="element-55">
      Your privacy is important to us. This Privacy Policy explains how we
      collect, use, and protect your personal information when you purchase and
      use The No-Gym Mobility Matrix digital product.
    </p>
    <p className="text-white font-semibold" data-id="element-56">Information We Collect</p>
    <p data-id="element-57">
      When you purchase our product, we collect the information necessary to
      process your transaction, including your name and email address. We do not
      collect any health or medical data through our website.
    </p>
    <p className="text-white font-semibold" data-id="element-58">How We Use Your Information</p>
    <p data-id="element-59">
      We use your information solely to deliver the digital product you
      purchased and to send you important updates related to your purchase. We
      will never sell, rent, or share your personal information with third
      parties for marketing purposes.
    </p>
    <p className="text-white font-semibold" data-id="element-60">Data Security</p>
    <p data-id="element-61">
      We implement industry-standard security measures to protect your personal
      information. All transactions are processed through secure, encrypted
      connections.
    </p>
    <p className="text-white font-semibold" data-id="element-62">Cookies</p>
    <p data-id="element-63">
      Our website may use cookies to improve your browsing experience. You can
      disable cookies in your browser settings at any time.
    </p>
    <p className="text-white font-semibold" data-id="element-64">Your Rights</p>
    <p data-id="element-65">
      You have the right to access, correct, or delete your personal information
      at any time. To exercise these rights, please reach out through the
      channels provided with your purchase.
    </p>
    <p className="text-white font-semibold" data-id="element-66">Changes to This Policy</p>
    <p data-id="element-67">
      We may update this Privacy Policy from time to time. Any changes will be
      posted on this page with an updated revision date.
    </p>
  </>;
const termsContent = <>
    <p className="text-white font-semibold text-base" data-id="element-68">Terms and Conditions</p>
    <p data-id="element-69">
      Last updated:{' '}
      {new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })}
    </p>
    <p data-id="element-70">
      By purchasing and using The No-Gym Mobility Matrix, you agree to the
      following terms and conditions.
    </p>
    <p className="text-white font-semibold" data-id="element-71">Digital Product License</p>
    <p data-id="element-72">
      Upon purchase, you are granted a personal, non-transferable, non-exclusive
      license to use The No-Gym Mobility Matrix for your own personal use. You
      may not redistribute, resell, or share the product with others.
    </p>
    <p className="text-white font-semibold" data-id="element-73">Medical Disclaimer</p>
    <p data-id="element-74">
      The No-Gym Mobility Matrix is an educational product and is not intended
      as a substitute for professional medical advice, diagnosis, or treatment.
      Always consult your physician or qualified health provider before
      beginning any new exercise or mobility program, especially if you have
      existing medical conditions.
    </p>
    <p className="text-white font-semibold" data-id="element-75">Results Disclaimer</p>
    <p data-id="element-76">
      Individual results may vary. The timelines and outcomes described in our
      materials are based on typical user experiences but are not guaranteed.
      Your results will depend on your individual condition, consistency, and
      other factors.
    </p>
    <p className="text-white font-semibold" data-id="element-77">Refund Policy</p>
    <p data-id="element-78">
      Due to the digital nature of this product, all sales are final. If you
      experience technical issues accessing your purchase, we will work to
      resolve them promptly.
    </p>
    <p className="text-white font-semibold" data-id="element-79">Limitation of Liability</p>
    <p data-id="element-80">
      To the fullest extent permitted by law, The No-Gym Mobility Matrix and its
      creators shall not be liable for any injury, loss, or damage arising from
      the use of this product. You assume full responsibility for your use of
      the materials provided.
    </p>
    <p className="text-white font-semibold" data-id="element-81">Governing Law</p>
    <p data-id="element-82">
      These terms shall be governed by and construed in accordance with
      applicable laws. Any disputes arising from these terms will be resolved
      through appropriate legal channels.
    </p>
  </>;
export function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="w-full bg-dark-panel py-12 border-t border-dark-lighter text-center" data-id="element-83">
        <div className="max-w-4xl mx-auto px-6" data-id="element-84">
          <p className="font-display uppercase tracking-widest text-cyan-accent mb-6 text-xl" data-id="element-85">
            The No-Gym Mobility Matrix
          </p>
          <p className="text-gray-500 text-sm mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-86">
            Disclaimer: The information provided in this program is for
            educational purposes only and is not intended as a substitute for
            professional medical advice, diagnosis, or treatment. Always seek
            the advice of your physician or other qualified health provider with
            any questions you may have regarding a medical condition.
          </p>
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-87">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-400 hover:text-cyan-accent transition-colors text-sm underline underline-offset-4" data-id="element-88">
              Privacy Policy
            </button>
            <span className="text-dark-lighter" data-id="element-89">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-400 hover:text-cyan-accent transition-colors text-sm underline underline-offset-4" data-id="element-90">
              Terms & Conditions
            </button>
          </div>
          <p className="text-gray-600 text-sm" data-id="element-91">
            &copy; {new Date().getFullYear()} The No-Gym Mobility Matrix. All
            rights reserved.
          </p>
        </div>
      </footer>

      {activeModal === 'privacy' && <PolicyModal title="Privacy Policy" content={privacyContent} onClose={() => setActiveModal(null)} data-id="element-92" />}
      {activeModal === 'terms' && <PolicyModal title="Terms & Conditions" content={termsContent} onClose={() => setActiveModal(null)} data-id="element-93" />}
    </>;
}